import {
  MembershipStatusEnum,
  PermissionEnum,
  Workspace,
} from '@sr-sdks/api-sdk-axios';
import { useParams } from 'react-router-dom';
import { SUPPORT_EMAIL } from '../constant/Common';
import { isWorkspaceReadyForMembership } from '../forms/workspaceDetails/shared/membershipReadiness';
import { userService } from '../reducers/permissions/userSlice';
import { isMembershipDenial } from '../routes/RequireCurrentMember';
import { useTypedSelector } from '../stateStore';
import {
  MEMBERSHIP_UNDER_REVIEW_STATUSES,
  isAllowedToApplyMembership,
} from '../utils/helpers/common';
import { useActiveWorkspace } from './useActiveWorkspace';
import { useCheckUserPermissions } from './useCheckUserPermissions';
import { useEffectiveMembershipStatus } from './useEffectiveMembershipStatus';
import { useWorkspaceMembership } from './useWorkspaceMembership';

const MEMBERSHIP_IN_REVIEW_TOOLTIP =
  'Membership currently in review. Our team will review your workspace details and Membership Agreement for compliance (typical turnaround time: 1-2 business days).';
const MEMBERSHIP_AWAITING_UPDATE_TOOLTIP =
  'Screenrights needs more information before this membership can be approved. Open Membership Details in workspace settings to respond.';
const MEMBERSHIP_INACTIVE_TOOLTIP = `This workspace is not a current Screenrights member. Contact ${SUPPORT_EMAIL} to restore membership.`;
const MEMBERSHIP_APPLICATION_SENT_TOOLTIP =
  'A workspace owner or admin needs to accept the Membership Agreement before this application goes to Screenrights for review.';
const MEMBERSHIP_UNKNOWN_TOOLTIP =
  'Membership for this workspace could not be confirmed. Refresh the page and try again.';
const MEMBERSHIP_PERMISSION_TOOLTIP =
  'Ask a workspace owner or admin to apply for Screenrights membership.';
const WORKSPACE_DETAILS_INCOMPLETE_TOOLTIP =
  "Please make sure all of your workspace details are up to date, we've noticed information is missing.";

/**
 * Why registration is closed for a workspace that is neither a current member
 * nor able to start an application. Without this split every one of those
 * statuses reads as "currently in review" — including a rejection, a
 * resignation, and `AwaitingUpdate`, where staff are in fact waiting on the
 * user and `MembershipActionsMenu` offers them a "Submit Update" action.
 */
const getMembershipBlockedReason = (membershipStatus: MembershipStatusEnum) => {
  if (MEMBERSHIP_UNDER_REVIEW_STATUSES.includes(membershipStatus)) {
    return MEMBERSHIP_IN_REVIEW_TOOLTIP;
  }

  // `ApplicationSent` is not yet with staff: nothing moves to `InReview` until
  // an owner or admin accepts the agreement, which is what
  // `MembershipApplicationGate` force-opens for them. Saying "in review" here
  // would leave everyone else waiting on a review that has not started.
  if (membershipStatus === MembershipStatusEnum.ApplicationSent) {
    return MEMBERSHIP_APPLICATION_SENT_TOOLTIP;
  }

  if (membershipStatus === MembershipStatusEnum.AwaitingUpdate) {
    return MEMBERSHIP_AWAITING_UPDATE_TOOLTIP;
  }

  return MEMBERSHIP_INACTIVE_TOOLTIP;
};

export type RoyaltiesRegistrationGate =
  /** `retry` only when the check itself failed, so trying again may help. */
  | { reason: string; retry?: () => void; status: 'blocked' }
  /** Could apply, but the workspace details an application needs are missing. */
  | { reason: string; status: 'incomplete' }
  | { status: 'allowed' }
  /** Not a member yet, and this user may apply on the workspace's behalf. */
  | { status: 'apply'; workspace: Workspace }
  | { status: 'pending' };

/**
 * Whether the active workspace may make a secondary-royalties registration,
 * which only a Current Member may do. Shared by every entry point to one, so
 * they cannot disagree on who gets through.
 */
export const useRoyaltiesRegistrationGate = (): RoyaltiesRegistrationGate => {
  const { workspaceId } = useParams();
  const [workspace, activeWorkspaceQuery] = useActiveWorkspace();
  const currentUser = useTypedSelector(userService.selectors.userMe);
  const {
    hasBlockingWorkspaceRefreshError,
    isEnabled: hasMembershipQuery,
    isResolving,
    query: membershipQuery,
    retryWorkspaceRefresh,
  } = useWorkspaceMembership(workspace);
  const { isCurrentMember, membershipStatus } = useEffectiveMembershipStatus(
    workspace,
    membershipQuery.data,
  );
  // The membership record outranks a lagging workspace cache in both
  // directions, so the apply gate has to read the resolved status as well —
  // otherwise a workspace staff have just rejected is offered the application
  // again. Same shape `MembershipDetailsCard` hands `MembershipActionsMenu`.
  const workspaceWithEffectiveMembershipStatus = workspace && {
    ...workspace,
    membershipStatus,
  };
  const canApplyForMembership = isAllowedToApplyMembership(
    workspaceWithEffectiveMembershipStatus,
    currentUser,
  );
  // Applying commits the whole workspace to a membership agreement, so it takes
  // the same permission the other customer-facing Apply trigger does:
  // `MembershipDetailsCard` wraps `MembershipActionsMenu` in
  // `ProtectElement permissionRequired={[EditWorkspaceDetails]}`.
  const canApplyOnBehalfOfWorkspace = useCheckUserPermissions(
    [PermissionEnum.EditWorkspaceDetails],
    currentUser,
    workspaceId,
  );

  if (activeWorkspaceQuery.isPending || isResolving) {
    return { status: 'pending' };
  }

  // Without a workspace, or after a failed refresh, the status can't be trusted:
  // block with a retry rather than tell a member their membership lapsed.
  if (!workspace || !workspaceWithEffectiveMembershipStatus) {
    return {
      reason: MEMBERSHIP_UNKNOWN_TOOLTIP,
      retry: () => void activeWorkspaceQuery.refetch(),
      status: 'blocked',
    };
  }

  if (hasBlockingWorkspaceRefreshError) {
    return {
      reason: MEMBERSHIP_UNKNOWN_TOOLTIP,
      retry: retryWorkspaceRefresh,
      status: 'blocked',
    };
  }

  // As in `RequireCurrentMember`: a failed record fetch is not a verdict, and
  // the cached status it falls back on may lag an approval.
  if (
    hasMembershipQuery &&
    membershipQuery.isError &&
    !membershipQuery.data &&
    !isCurrentMember &&
    !isMembershipDenial(membershipQuery.error)
  ) {
    return {
      reason: MEMBERSHIP_UNKNOWN_TOOLTIP,
      retry: () => void membershipQuery.refetch(),
      status: 'blocked',
    };
  }

  if (isCurrentMember) {
    return { status: 'allowed' };
  }

  if (canApplyForMembership) {
    if (!canApplyOnBehalfOfWorkspace) {
      return { reason: MEMBERSHIP_PERMISSION_TOOLTIP, status: 'blocked' };
    }

    // The query's own object, so the readiness answer is shared between renders.
    return isWorkspaceReadyForMembership(workspace)
      ? { status: 'apply', workspace: workspaceWithEffectiveMembershipStatus }
      : { reason: WORKSPACE_DETAILS_INCOMPLETE_TOOLTIP, status: 'incomplete' };
  }

  // MembershipApplicationGate holds a staff-sent agreement back until the
  // details are complete, so "accept the agreement" would send them nowhere.
  const isStaffSentMissingDetails =
    membershipStatus === MembershipStatusEnum.ApplicationSent &&
    canApplyOnBehalfOfWorkspace &&
    !isWorkspaceReadyForMembership(workspace);

  return {
    reason: isStaffSentMissingDetails
      ? WORKSPACE_DETAILS_INCOMPLETE_TOOLTIP
      : getMembershipBlockedReason(membershipStatus),
    status: 'blocked',
  };
};
